<template>
  <div class="media-page">
    <div class="filter-bar">
      <div class="search-box">
        <input v-model="searchKeyword" type="text" placeholder="搜索图片名称..." class="search-input" />
        <button @click="searchMedia" class="search-btn">搜索</button>
      </div>
      <button @click="uploadMedia" class="upload-btn">+ 上传图片</button>
    </div>
    
    <div class="media-grid">
      <div 
        v-for="media in mediaList" 
        :key="media.id" 
        class="media-item"
        :class="{ selected: selectedIds.includes(media.id) }"
        @click="toggleSelect(media.id)"
      >
        <img :src="media.url" class="media-thumb" />
        <div class="media-info">
          <span class="media-name">{{ media.name }}</span>
          <span class="media-size">{{ formatSize(media.size) }}</span>
        </div>
        <div v-if="selectedIds.includes(media.id)" class="selected-mark">✓</div>
      </div>
    </div>

    <div v-if="showUploadModal" class="modal-overlay" @click="closeUploadModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>上传图片</h3>
          <button @click="closeUploadModal" class="close-btn">×</button>
        </div>
        <div class="modal-body">
          <div class="upload-area" @dragover.prevent @drop.prevent="handleDrop">
            <div v-if="uploadPreview" class="upload-preview">
              <img :src="uploadPreview" class="preview-img" />
              <button @click.stop="clearPreview" class="remove-btn">×</button>
            </div>
            <div v-else class="upload-placeholder">
              <div class="upload-icon">📷</div>
              <p>点击或拖拽上传图片</p>
              <p class="hint">支持 JPG、PNG 格式，大小不超过 5MB</p>
            </div>
            <input ref="uploadInput" type="file" accept="image/jpeg,image/png" class="file-input" @change="handleFileSelect" />
          </div>
          <div class="form-group">
            <label>图片名称</label>
            <input v-model="uploadForm.name" type="text" class="form-control" placeholder="请输入图片名称" />
          </div>
          <div class="form-group">
            <label>业务分类</label>
            <select v-model="uploadForm.category" class="form-control">
              <option v-for="opt in categoryOptions" :key="opt.value" :value="opt.value">
                {{ opt.label }}
              </option>
            </select>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeUploadModal" class="btn btn-secondary">取消</button>
          <button @click="saveMedia" class="btn btn-primary">上传</button>
        </div>
      </div>
    </div>

    <div v-if="selectedIds.length > 0" class="batch-bar">
      <span>已选择 {{ selectedIds.length }} 张图片</span>
      <button @click="batchDelete" class="batch-btn delete">批量删除</button>
    </div>
  </div>
</template>

<script>
import { getMediaList, uploadMedia, batchDeleteMedia } from '@/api/media'

export default {
  name: 'Media',
  data() {
    return {
      searchKeyword: '',
      mediaList: [],
      selectedIds: [],
      showUploadModal: false,
      uploadPreview: '',
      uploadFile: null,
      uploadForm: {
        name: '',
        category: 'default'
      },
      // 业务分类枚举，须与后端 MediaController::CATEGORY_CONFIG 保持一致
      categoryOptions: [
        { value: 'product', label: '商品图片' },
        { value: 'banner', label: '首页轮播图' },
        { value: 'ad-home', label: '首页广告' },
        { value: 'ad-product-category', label: '商品分类页广告' },
        { value: 'ad-product-detail', label: '商品详情页广告' },
        { value: 'product-category', label: '商品分类图' },
        { value: 'default', label: '通用素材' }
      ]
    }
  },
  created() {
    this.loadMediaList()
  },
  methods: {
    async loadMediaList() {
      try {
        const params = this.searchKeyword ? { keyword: this.searchKeyword } : {}
        const response = await getMediaList(params)
        if (response.code === 0) {
          this.mediaList = response.data.data || response.data
        } else {
        }
      } catch (error) {
      }
    },
    searchMedia() {
      this.loadMediaList()
    },
    uploadMedia() {
      this.uploadPreview = ''
      this.uploadFile = null
      this.uploadForm.name = ''
      this.showUploadModal = true
    },
    closeUploadModal() {
      this.showUploadModal = false
    },
    triggerFileInput() {
      this.$refs.uploadInput.click()
    },
    handleFileSelect(e) {
      const file = e.target.files[0]
      if (file) {
        this.processFile(file)
      }
    },
    handleDrop(e) {
      const file = e.dataTransfer.files[0]
      if (file && file.type.startsWith('image/')) {
        this.processFile(file)
      }
    },
    processFile(file) {
      if (!file.type.startsWith('image/')) {
        alert('请选择图片文件')
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('图片大小不能超过5MB')
        return
      }
      
      this.uploadFile = file
      const reader = new FileReader()
      reader.onload = (e) => {
        this.uploadPreview = e.target.result
        if (!this.uploadForm.name) {
          this.uploadForm.name = file.name.replace(/\.[^/.]+$/, '')
        }
      }
      reader.readAsDataURL(file)
    },
    clearPreview() {
      this.uploadPreview = ''
      this.uploadFile = null
    },
    async saveMedia() {
      if (!this.uploadFile) {
        alert('请选择要上传的图片')
        return
      }
      
      const formData = new FormData()
      formData.append('file', this.uploadFile)
      formData.append('name', this.uploadForm.name || this.uploadFile.name.replace(/\.[^/.]+$/, ''))
      formData.append('category', this.uploadForm.category)
      
      try {
        const response = await uploadMedia(formData)
        if (response.code === 0) {
          this.mediaList.unshift(response.data)
          this.showUploadModal = false
          alert('上传成功')
        } else {
          alert(response.message || '上传失败')
        }
      } catch (error) {
        alert('上传失败: ' + error.message)
      }
    },
    toggleSelect(id) {
      const index = this.selectedIds.indexOf(id)
      if (index !== -1) {
        this.selectedIds.splice(index, 1)
      } else {
        this.selectedIds.push(id)
      }
    },
    async batchDelete() {
      if (confirm(`确定要删除选中的 ${this.selectedIds.length} 张图片吗？`)) {
        const response = await batchDeleteMedia(this.selectedIds)
        if (response.code === 0) {
          this.mediaList = this.mediaList.filter(m => !this.selectedIds.includes(m.id))
          this.selectedIds = []
          alert('删除成功')
        } else {
          alert(response.message || '删除失败')
        }
      }
    },
    formatSize(bytes) {
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    }
  }
}
</script>

<style scoped>
.media-page {
  padding: 20px;
}

.filter-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.search-box {
  display: flex;
  gap: 10px;
}

.search-input {
  padding: 10px 16px;
  border: 1px solid #ddd;
  border-radius: 6px;
  width: 250px;
}

.search-btn {
  padding: 10px 20px;
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 6px;
  cursor: pointer;
}

.upload-btn {
  padding: 10px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.media-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
}

.media-item {
  position: relative;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  cursor: pointer;
  transition: all 0.3s ease;
}

.media-item:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 16px rgba(0,0,0,0.1);
}

.media-item.selected {
  border: 2px solid #667eea;
}

.media-thumb {
  width: 100%;
  height: 150px;
  object-fit: cover;
}

.media-info {
  padding: 12px;
}

.media-name {
  display: block;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.media-size {
  display: block;
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

.selected-mark {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  background: #667eea;
  color: #fff;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 14px;
}

.batch-bar {
  position: fixed;
  bottom: 0;
  left: 200px;
  right: 0;
  background: #fff;
  padding: 15px 20px;
  box-shadow: 0 -2px 10px rgba(0,0,0,0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.batch-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.batch-btn.delete {
  background: #e74c3c;
  color: #fff;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  overflow: auto;
}

.modal-content {
  background: #fff;
  border-radius: 12px;
  width: 500px;
  max-width: 90%;
  position: relative;
  z-index: 1001;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
}

.close-btn {
  font-size: 24px;
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
}

.modal-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: #666;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
}

.upload-area {
  position: relative;
  border: 2px dashed #ddd;
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  margin-bottom: 20px;
}

.upload-area:hover {
  border-color: #667eea;
  background: #f8f9ff;
}

.upload-placeholder {
  color: #999;
}

.upload-icon {
  font-size: 48px;
  margin-bottom: 12px;
}

.upload-placeholder p {
  margin: 8px 0;
}

.upload-placeholder .hint {
  font-size: 12px;
  color: #ccc;
}

.upload-preview {
  position: relative;
}

.preview-img {
  max-width: 100%;
  max-height: 200px;
  border-radius: 8px;
}

.remove-btn {
  position: absolute;
  top: -12px;
  right: -12px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #e74c3c;
  color: #fff;
  border: none;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
}

.file-input {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  padding: 20px;
  border-top: 1px solid #eee;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
}

.btn-secondary {
  background: #f5f5f5;
  color: #666;
}
</style>